import { Button } from "@somethings-ui/web";
import { Scribbles } from "@somethings-ui/core/assets";
import { PlayIcon } from "@heroicons/react/24/solid";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { Loader2 } from "lucide-react";
import { TrainingCompletionShell } from "./training-completion-shell.tsx";

interface TrainingModuleCompleteNextModule {
  position: number;
  title: string;
  description: string | null;
}

interface TrainingModuleCompleteProps {
  modulePosition: number;
  moduleTitle: string;
  summary: string | null;
  nextModule: TrainingModuleCompleteNextModule | null;
  isContinuing: boolean;
  onContinue: () => void;
  onBackToTraining: () => void;
}

export function TrainingModuleComplete({
  modulePosition,
  moduleTitle,
  summary,
  nextModule,
  isContinuing,
  onContinue,
  onBackToTraining,
}: TrainingModuleCompleteProps) {
  return (
    <TrainingCompletionShell
      bgClassName="bg-brand-forest"
      numberLabel={String(modulePosition).padStart(2, "0")}
      title="Module complete"
      subtitle={moduleTitle}
      body={
        summary ? (
          summary
        ) : (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" />
            Putting together your summary…
          </span>
        )
      }
      decorations={
        <>
          <img
            src={Scribbles.blackSwoosh}
            alt=""
            aria-hidden
            className="pointer-events-none absolute -right-80 -top-60 w-[800px] max-w-none -rotate-45 select-none"
          />
          <img
            src={Scribbles.blackSwoosh}
            alt=""
            aria-hidden
            className="pointer-events-none absolute -bottom-60 -left-80 w-[800px] max-w-none -rotate-45 select-none"
          />
        </>
      }
    >
      <div className="flex w-full max-w-xl flex-col gap-3">
        {nextModule && (
          <div className="relative flex flex-col gap-5 overflow-hidden rounded-md bg-surfaces-z0 p-6">
            <img
              src={Scribbles.blueStarburst}
              alt=""
              aria-hidden
              className="pointer-events-none absolute -right-48 top-1/2 w-96 max-w-none -translate-y-1/2 rotate-12 select-none"
            />
            <div className="relative z-10 flex flex-col gap-3 text-text-primary">
              <span className="text-body-3 opacity-75">
                Module {nextModule.position}
              </span>
              <span className="text-header-5">{nextModule.title}</span>
              {nextModule.description && (
                <span className="text-body-3 opacity-75">
                  {nextModule.description}
                </span>
              )}
            </div>
            <div className="relative z-10">
              <Button
                variant="primary"
                fullWidth={false}
                leadingIcon={<PlayIcon className="size-4" />}
                loading={isContinuing}
                onClick={onContinue}
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        <Button
          variant="secondary"
          fullWidth
          leadingIcon={<ArrowLeftIcon className="size-4" />}
          onClick={onBackToTraining}
        >
          Back to Training Room
        </Button>
      </div>
    </TrainingCompletionShell>
  );
}
